import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Tag, ChevronRight, Loader2 } from 'lucide-react';
import DOMPurify from 'dompurify';
import SEO from '../components/SEO';
import JsonLd, { articleSchema, breadcrumbSchema } from '../components/JsonLd';
import { devError } from '../utils/devLog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Allow iframes only from trusted video hosts. Anything else gets stripped.
const TRUSTED_IFRAME_HOSTS = [
  'www.youtube.com',
  'www.youtube-nocookie.com',
  'youtube.com',
  'player.vimeo.com',
  'vimeo.com',
];

DOMPurify.addHook('uponSanitizeElement', (node, data) => {
  if (data.tagName !== 'iframe') return;
  try {
    const src = node.getAttribute('src') || '';
    const url = new URL(src, window.location.origin);
    if (!TRUSTED_IFRAME_HOSTS.includes(url.hostname)) {
      node.parentNode?.removeChild(node);
    }
  } catch {
    node.parentNode?.removeChild(node);
  }
});

const ResourcePage = () => {
  const { slug } = useParams();
  const [resource, setResource] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Scroll to top whenever we navigate to a new post (prev/next links, direct URL, etc.)
    window.scrollTo({ top: 0, behavior: 'auto' });

    const controller = new AbortController();
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch all posts first
        const postsRes = await fetch(`${API}/posts`, { signal: controller.signal });
        const postsData = await postsRes.json();
        setAllPosts(postsData);

        // Then fetch specific post
        const res = await fetch(`${API}/posts/${slug}`, { signal: controller.signal });
        if (res.ok) {
          const data = await res.json();
          setResource(data);
        }
      } catch (error) {
        if (error.name !== 'AbortError') devError('Error fetching resource:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const getImageUrl = (img) => img?.startsWith('http') ? img : `${BACKEND_URL}${img}`;

  // Memoize navigation posts to prevent recalculation on every render
  const navigationPosts = useMemo(() => {
    const currentIndex = allPosts.findIndex((r) => r.slug === slug);
    return {
      prev: currentIndex > 0 ? allPosts[currentIndex - 1] : null,
      next: currentIndex < allPosts.length - 1 ? allPosts[currentIndex + 1] : null
    };
  }, [allPosts, slug]);

  // Memoize "More Resources" sidebar list
  const sidebarPosts = useMemo(
    () => allPosts.filter((p) => p.slug !== slug).slice(0, 3),
    [allPosts, slug]
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00BCE4] animate-spin" />
      </div>
    );
  }

  if (!resource) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Resource Not Found</h1>
          <Link to="/" className="text-[#00BCE4] hover:underline">Return Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 lg:pt-40 pb-16 bg-gray-50">
      <SEO
        title={resource.title}
        description={resource.excerpt || resource.title}
        image={resource.image ? getImageUrl(resource.image) : undefined}
        url={`https://imprint-mis.co.uk/resources/${resource.slug}`}
        type="article"
        publishedTime={resource.created_at}
        modifiedTime={resource.updated_at}
      />
      <JsonLd data={articleSchema(resource, resource.image ? getImageUrl(resource.image) : null)} />
      <JsonLd data={breadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: 'Resources', path: '/resources' },
        { name: resource.title, path: `/resources/${resource.slug}` },
      ])} />
      <div className="section-container">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
          <Link to="/" className="hover:text-[#00BCE4] transition-colors">Home</Link>
          <ChevronRight size={16} />
          <Link to="/resources" className="hover:text-[#00BCE4] transition-colors">Resources</Link>
          <ChevronRight size={16} />
          <span className="text-gray-900">{resource.title}</span>
        </nav>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Hero Image */}
            {resource.image && (
              <div className="w-full aspect-[16/10] rounded-2xl overflow-hidden mb-8 shadow-lg">
                <img
                  src={getImageUrl(resource.image)}
                  alt={resource.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <div className="flex items-center gap-2 text-sm text-gray-500" style={{ fontFamily: 'Inter, sans-serif' }}>
                <Calendar size={16} />
                <span>{new Date(resource.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              </div>
              {resource.category && (
                <div className="flex items-center gap-2">
                  <Tag size={16} className="text-[#00BCE4]" />
                  <span className="text-sm font-semibold text-[#00BCE4]" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {resource.category}
                  </span>
                </div>
              )}
            </div>

            {/* Title */}
            <h1
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {resource.title}
            </h1>

            {/* Excerpt */}
            {resource.excerpt && (
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                {resource.excerpt}
              </p>
            )}

            <div className="border-t border-gray-200 my-8"></div>

            {/* Body content (HTML from WYSIWYG) - SANITIZED */}
            <div
              className="prose prose-lg max-w-none text-gray-600 prose-a:text-[#00BCE4] prose-a:font-semibold prose-a:underline prose-a:underline-offset-2 hover:prose-a:text-[#0092b3] prose-headings:text-gray-900 prose-strong:text-gray-900 prose-blockquote:border-l-[#00BCE4] prose-img:rounded-xl"
              style={{ fontFamily: 'Inter, sans-serif' }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(resource.body, {
                  ADD_TAGS: ['iframe'],
                  ADD_ATTR: [
                    'allow',
                    'allowfullscreen',
                    'frameborder',
                    'scrolling',
                    'src',
                    'sandbox',
                    'loading',
                    'referrerpolicy',
                  ],
                  ALLOWED_URI_REGEXP:
                    /^(?:(?:https?|mailto|ftp|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
                  ALLOW_UNKNOWN_PROTOCOLS: false,
                  // Hook prunes any iframe whose src isn't from a trusted host.
                }),
              }}
            />
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-28 space-y-8">
              {/* Recent Posts */}
              {allPosts.length > 1 && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                  <h3
                    className="text-xl font-bold text-gray-900 mb-4"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    More Resources
                  </h3>
                  <div className="space-y-4">
                    {sidebarPosts.map((post) => (
                        <Link
                          key={post.id}
                          to={`/resources/${post.slug}`}
                          className="block group"
                        >
                          <div className="flex gap-3">
                            {post.image && (
                              <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                                <img
                                  src={getImageUrl(post.image)}
                                  alt={post.title}
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                />
                              </div>
                            )}
                            <div>
                              <h4
                                className="font-semibold text-gray-900 group-hover:text-[#00BCE4] transition-colors text-sm mb-1"
                                style={{ fontFamily: 'Outfit, sans-serif' }}
                              >
                                {post.title}
                              </h4>
                              <p className="text-xs text-gray-500" style={{ fontFamily: 'Inter, sans-serif' }}>
                                {new Date(post.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </p>
                            </div>
                          </div>
                        </Link>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Navigation */}
        {(navigationPosts.prev || navigationPosts.next) && (
          <div className="mt-16 pt-8 border-t border-gray-200">
            <div className="grid md:grid-cols-2 gap-6">
              {navigationPosts.prev && (
                <Link
                  to={`/resources/${navigationPosts.prev.slug}`}
                  className="group flex items-center gap-4 p-6 bg-white rounded-xl border border-gray-200 hover:border-[#00BCE4] hover:shadow-lg transition-all"
                >
                  <ArrowLeft size={24} className="text-[#00BCE4] flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>Previous</p>
                    <p
                      className="font-semibold text-gray-900 group-hover:text-[#00BCE4] transition-colors"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      {navigationPosts.prev.title}
                    </p>
                  </div>
                </Link>
              )}

              {navigationPosts.next && (
                <Link
                  to={`/resources/${navigationPosts.next.slug}`}
                  className="group flex items-center justify-end gap-4 p-6 bg-white rounded-xl border border-gray-200 hover:border-[#00BCE4] hover:shadow-lg transition-all md:col-start-2"
                >
                  <div className="text-right">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>Next</p>
                    <p
                      className="font-semibold text-gray-900 group-hover:text-[#00BCE4] transition-colors"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      {navigationPosts.next.title}
                    </p>
                  </div>
                  <ArrowRight size={24} className="text-[#00BCE4] flex-shrink-0" />
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResourcePage;
